import { memo, useState } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { motion } from "motion/react"
import { IconSidebarRightOutline18 as PanelRight } from "nucleo-ui-outline-18"

import type { CustomView } from "@productlane/zero/schema"
import { IconButton, useBreakpoints } from "@productlane/ui"

import type { RoadmapTab } from "./nav-items"
import type { PortalTreeOption } from "./portal-tree"
import { PageHeader } from "../page-header"
import { AddDropdown } from "./add-dropdown"
import { PortalTree } from "./portal-tree"
import { RoadmapViewsNav } from "./roadmap-views-nav"

export const RoadmapNav = memo(
	({
		tabValue,
		onTabChange,
		portals,
		activePortalId,
		onPortalChange,
		pendingCount,
		isPendingCountCapped,
		onCreateProject,
		onCreateIssue,
		onImportIssues,
		isLinearConnected,
		onOpenView,
	}: {
		tabValue: RoadmapTab
		onTabChange: (value: RoadmapTab) => void
		portals: PortalTreeOption[]
		activePortalId: string | null | undefined
		onPortalChange: (id: string | null) => void
		pendingCount: number
		isPendingCountCapped?: boolean
		onCreateProject?: () => void
		onCreateIssue?: () => void
		onImportIssues?: () => void
		isLinearConnected?: boolean
		onOpenView: (view: CustomView) => void
	}) => {
		const { isMobile } = useBreakpoints()
		const [isNavOpen, setIsNavOpen] = useState(false)

		const closeMobileNav = () => {
			if (isNavOpen) setIsNavOpen(false)
		}

		return (
			<>
				{!isMobile && (
					<div className="hidden h-full w-64 flex-shrink-0 flex-col overflow-y-auto border-r bg-white sm:flex dark:bg-gray-900">
						<PageHeader
							title="Feature requests"
							clearsNavToggle
							className="border-b lg:pl-2.5 lg:pr-3"
							titleClassName="lg:ml-[7.5px]"
							actions={
								onCreateProject && onCreateIssue && onImportIssues ? (
									<AddDropdown
										compact
										onCreateProject={onCreateProject}
										onCreateIssue={onCreateIssue}
										onImportIssues={onImportIssues}
										isLinearConnected={isLinearConnected ?? false}
									/>
								) : undefined
							}
						/>
						<PortalTree
							portals={portals}
							activePortalId={activePortalId}
							onPortalChange={onPortalChange}
							tabValue={tabValue}
							onTabChange={onTabChange}
							pendingCount={pendingCount}
							isPendingCountCapped={isPendingCountCapped}
						/>
						<RoadmapViewsNav onOpenView={onOpenView} />
					</div>
				)}

				{isMobile && (
					<div className="relative sm:hidden">
						<div className="absolute left-2 top-3.5 z-50 flex items-center gap-0.5">
							<IconButton
								variant="ghost"
								className="size-7"
								onClick={() => setIsNavOpen(!isNavOpen)}
								aria-label="Toggle requests navigation"
							>
								<PanelRight className="size-4" />
							</IconButton>
						</div>
						{isNavOpen && (
							<DialogPrimitive.Root
								modal={false}
								onOpenChange={(open) => {
									setIsNavOpen(open)
								}}
							>
								<DialogPrimitive.Portal forceMount>
									<DialogPrimitive.Overlay
										forceMount
										asChild
										className="sm:hidden"
									>
										<motion.div
											className="pointer-events-none fixed inset-0 top-12 bg-black/50"
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{ duration: 0.1 }}
											exit={{ opacity: 0 }}
										/>
									</DialogPrimitive.Overlay>
									<DialogPrimitive.Content
										forceMount
										asChild
										className="sm:hidden"
									>
										<div className="fixed inset-0 top-12 flex h-full w-full flex-1 flex-col">
											<div className="flex h-full w-full flex-1 flex-col items-start bg-gray-25 shadow-xl dark:bg-gray-900">
												<PageHeader
													title="Feature requests"
													className="flex flex-row border-b py-2 pl-4"
												/>
												<div className="w-full flex-1 overflow-y-auto pb-2">
													<PortalTree
														portals={portals}
														activePortalId={activePortalId}
														onPortalChange={onPortalChange}
														tabValue={tabValue}
														onTabChange={onTabChange}
														pendingCount={pendingCount}
														isPendingCountCapped={isPendingCountCapped}
														onSelect={closeMobileNav}
													/>
													<RoadmapViewsNav
														onOpenView={onOpenView}
														onSelect={closeMobileNav}
													/>
												</div>
											</div>
										</div>
									</DialogPrimitive.Content>
								</DialogPrimitive.Portal>
							</DialogPrimitive.Root>
						)}
					</div>
				)}
			</>
		)
	},
)
